import { useMemo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import { useSearchQuery } from '@platform/state/api';
import { recordCode } from '../../form/header/recordCode';
import type { IRelatedFinderProps } from './IRelatedFinderProps';
import { useDebounced } from './useDebounced';
import { useForeignRecords } from './useForeignRecords';

/**
 * Комбобокс «Введіть код або назву, щоб знайти елемент…» (прототип v4, вкладка «Пов’язані елементи»): пошук записів усіх модулів робочого
 * простору під час введення; список — чип типу, номер (без префікса, як у прототипі), назва; підвал «N елементів відповідають» і «Розширений пошук…»; Enter вибирає
 * виділений (перший) збіг, ↑ ↓ — виділення, Esc закриває; «…» праворуч відкриває розширений пошук.
 */
export function RelatedFinder({ ws, exclude, picked, onPick, onAdvanced }: IRelatedFinderProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const q = useDebounced(query.trim());
  const { data, isFetching } = useSearchQuery({ q, workspaceId: ws }, { skip: !open || !q });
  const hits = (data?.groups.flatMap((g) => g.hits) ?? []).filter((h) => !exclude.has(h.recordId));
  const foreign = useForeignRecords(ws, useMemo(() => (data?.groups ?? []).map((g) => g.module), [data]));
  const pick = (index: number) => { const hit = hits[index]; if (hit) { onPick(hit); setOpen(false); setQuery(''); } };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); setHi((h) => Math.max(0, Math.min(hits.length - 1, h + (e.key === 'ArrowDown' ? 1 : -1)))); }
    if (e.key === 'Enter' && hits.length) { e.preventDefault(); pick(hi); }
  };
  return (
    <div className="rtab-finder">
      <div className="rtab-finder__field">
        <input className="rtab-finder__input" role="combobox" aria-expanded={open && Boolean(q)} aria-label={t('recordPage.related.findLabel')} data-link-finder
          placeholder={t('recordPage.related.find')} value={open ? query : picked ? `${recordCode(picked.display)} · ${picked.title}` : ''} autoComplete="off"
          onFocus={() => { setOpen(true); setHi(0); }} onBlur={() => setTimeout(() => setOpen(false), 180)} onKeyDown={onKeyDown}
          onChange={(e) => { setQuery(e.target.value); setHi(0); setOpen(true); if (picked) onPick(null); }} />
        <button type="button" className="rtab-finder__more" title={t('recordPage.related.advanced')} aria-label={t('recordPage.related.advancedShort')} onClick={onAdvanced}>…</button>
      </div>
      {open && q && (
        <div className="rtab-finder__list" role="listbox">
          {hits.slice(0, 10).map((h, i) => (
            <button key={h.recordId} type="button" role="option" aria-selected={i === hi} className={cx('rtab-finder__opt', i === hi && 'is-hi')}
              onMouseDown={(e) => { e.preventDefault(); pick(i); }} onMouseEnter={() => setHi(i)}>
              <span className="rtab-chip">{foreign.typeLabel(h.module, h.recordType)}</span>
              <span className="rtab-rel__code" title={h.display}>{recordCode(h.display)}</span>
              <span className="rtab-finder__title">{h.title}</span>
            </button>
          ))}
          {!hits.length && <div className="rtab-finder__empty">{isFetching ? t('app.loading') : t('recordPage.related.noMatches')}</div>}
          <div className="rtab-finder__foot">
            <span>{t('recordPage.related.matches', { count: hits.length })}</span>
            <button type="button" onMouseDown={(e) => { e.preventDefault(); setOpen(false); onAdvanced(); }}>{t('recordPage.related.advanced')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
